/**
 * What a screen shows when it fails to load or throws. The usual cause is a new deploy: a tab
 * opened before it asks for page files of the old build, which are gone. Then the page reloads by
 * itself once to get the new build; anything else says what went wrong and offers a reload.
 */
import { useEffect } from "react";
import { Link, useRouteError } from "react-router";
import { Empty } from "./ui";

const RELOADED_AT = "brecx.staleReloadAt";
/** One reload per this many milliseconds, so a file that is really missing cannot loop. */
const RELOAD_GAP_MS = 30_000;

/** The error of a page file that could not be loaded: a tab older than the deploy. */
export function isStaleBuild(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
    msg,
  );
}

/** Reload to get the new build, unless the page did that a moment ago. True when it reloads. */
export function reloadForNewBuild(now = Date.now()): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
  } catch {
    // no storage: still reload once; the gap guard is a nicety
  }
  if (now - last < RELOAD_GAP_MS) return false;
  try {
    sessionStorage.setItem(RELOADED_AT, String(now));
  } catch {
    // ignore
  }
  window.location.reload();
  return true;
}

export function RouteError() {
  const err = useRouteError();
  const stale = isStaleBuild(err);
  useEffect(() => {
    if (stale) reloadForNewBuild();
  }, [stale]);
  return (
    <div className="page">
      <Empty
        action={
          <div className="row">
            <button type="button" className="btn sm pri" onClick={() => window.location.reload()}>
              Reload the page
            </button>
            <Link className="btn sm" to="/">
              Go to Home
            </Link>
          </div>
        }
      >
        {stale
          ? "Brecx Ads was updated while this tab was open. Reloading to get the new version…"
          : `This screen ran into an error: ${err instanceof Error ? err.message : String(err)}`}
      </Empty>
    </div>
  );
}
